import { Navigate } from 'react-router-dom';
import { useApp } from '@/components/hooks/useApp';
import { useCanSeePortalAsesor } from '@/components/hooks/useCanSeePortalAsesor';

/**
 * Salida para una cuenta que existe pero no tiene ninguna empresa.
 *
 * Ese estado era un callejón sin salida: `requireCompany` vive en la cadena
 * global de `/api`, así que sin empresa activa TODA la API responde 403 — el
 * usuario aterrizaba en el Home y veía una pantalla de errores sin nada
 * accionable. Las tres puertas que en teoría creaban una empresa estaban las
 * tres cerradas (el "+" del switcher exige `ROLES_MANAGE`, vacío sin empresa;
 * `/mis-empresas` rebota; y `POST /api/companies` chocaba con el propio
 * `requireCompany`).
 *
 * Este gate NO pinta formulario: manda a `/bienvenida`, que es quien orquesta
 * el alta entera —simulación de ARIA, forma jurídica, nombre + CIF, áreas— y
 * donde la empresa nace de verdad. Tuvo un formulario propio durante unas horas
 * y pedía el nombre ANTES de que el usuario hubiera visto para qué sirve el
 * producto; el nombre pasó al paso de identidad, junto al CIF, que es el mismo
 * dato: quién eres fiscalmente.
 */
export default function FirstCompanyGate({ children }) {
  const { user, company, loading } = useApp();
  // Un asesor de despacho vive SIN empresa activa de forma legítima: su sitio es
  // `/portal-asesor`, desde donde entra a la de cada cliente. Atraparlo aquí le
  // haría crearse una empresa que no necesita. El predicado es el de siempre
  // (`canSeePortalAsesor`), no una condición nueva escrita a mano.
  const canSeePortal = useCanSeePortalAsesor();

  // Conservador mientras carga la sesión: enseñar el alta a quien SÍ tiene
  // empresa, aunque sea medio segundo, es peor que esperar.
  if (loading || !user) return children;
  if (company?.id) return children;
  if (canSeePortal) return children;
  return <Navigate to="/bienvenida" replace />;
}
